import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { SlotMuelle } from '@/types'
import { muellesApi } from '@/services/api'
import { Loader } from '@/components/common/Loader'
import { errorMuelleDe, type ErrorMuelle } from '@/hooks/useDockMutaciones'
import { CLAVE_CUADRO } from '@/hooks/useDockSchedule'

/**
 * Administrar los muelles de un almacén: darlos de alta, renombrarlos, cambiar
 * su orden y borrarlos.
 *
 * **Renombrar no es cosmético.** El camión guarda el nombre del muelle como
 * texto, no un lookup, así que el backend arrastra a los camiones y devuelve
 * cuántos ha movido. Eso se le enseña al usuario: acaba de mover ocho camiones y
 * tiene derecho a saberlo.
 *
 * **Borrar uno con camiones se rechaza** la primera vez, y se explica. Borrarlo
 * igualmente es una decisión, no un descuido, así que hay que tomarla dos veces.
 */
export function SlotsDrawer({ abierto, almacen, slots, dark, onCerrar }: {
  abierto: boolean
  almacen: string
  slots: SlotMuelle[]
  dark: boolean
  onCerrar: () => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const invalidar = () => qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === CLAVE_CUADRO })

  const [nuevo, setNuevo] = useState('')
  const [editando, setEditando] = useState<string | null>(null)
  const [nombreEdit, setNombreEdit] = useState('')
  const [error, setError] = useState<ErrorMuelle | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [confirmarBorrado, setConfirmarBorrado] = useState<string | null>(null)

  useEffect(() => {
    if (!abierto) { setError(null); setAviso(null); setConfirmarBorrado(null); setEditando(null) }
  }, [abierto])

  const alFallar = (e: unknown) => setError(errorMuelleDe(e))

  const crear = useMutation({
    mutationFn: () => muellesApi.crearSlot({
      nombre: nuevo.trim(),
      almacen,
      orden: (slots[slots.length - 1]?.orden ?? slots.length) + 1,
    }),
    onSuccess: () => { setNuevo(''); setError(null); invalidar() },
    onError: alFallar,
  })

  const renombrar = useMutation({
    mutationFn: (v: { id: string; nombre: string }) =>
      muellesApi.actualizarSlot(v.id, { nombre: v.nombre }),
    onSuccess: (r) => {
      setEditando(null); setError(null)
      const n = r.data.camionesArrastrados
      // Si se han movido camiones hay que decirlo: es el efecto que el usuario no
      // pidió pero necesitaba.
      setAviso(n > 0 ? t('muelles.slots.arrastrados', { count: n }) : null)
      invalidar()
    },
    onError: alFallar,
  })

  const mover = useMutation({
    mutationFn: (v: { id: string; orden: number }) =>
      muellesApi.actualizarSlot(v.id, { orden: v.orden }),
    onSuccess: () => invalidar(),
    onError: alFallar,
  })

  const borrar = useMutation({
    mutationFn: (v: { id: string; forzar: boolean }) => muellesApi.borrarSlot(v.id, v.forzar),
    onSuccess: (r) => {
      setConfirmarBorrado(null); setError(null)
      const n = r.data.camionesHuerfanos
      setAviso(n > 0 ? t('muelles.slots.huerfanos', { count: n }) : null)
      invalidar()
    },
    onError: (e) => {
      const err = errorMuelleDe(e)
      // El 409 no es un fallo: es la pregunta de si de verdad quiere.
      if (err.codigo === 'slot_con_camiones') { setConfirmarBorrado(String(err.valores?.camiones ?? '')); setError(err) }
      else alFallar(e)
    },
  })

  if (!abierto) return null

  const bdr = dark ? 'border-white/10' : 'border-slate-200'
  const campo = `flex-1 px-2 py-1.5 text-[12px] rounded border ${
    dark ? 'bg-slate-800 border-white/10 text-slate-100' : 'bg-white border-slate-300 text-slate-900'
  }`
  const ocupado = crear.isPending || renombrar.isPending || borrar.isPending || mover.isPending

  const delCatalogo = slots.filter((s) => s.enCatalogo && s.id)

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={ocupado ? undefined : onCerrar} />
      <div className={`relative w-full max-w-sm h-full overflow-y-auto shadow-xl ${dark ? 'bg-slate-900' : 'bg-white'}`}>
        <header className={`sticky top-0 px-4 py-3 border-b ${bdr} ${dark ? 'bg-slate-900' : 'bg-white'}`}>
          <h2 className={`text-[14px] font-bold ${dark ? 'text-slate-100' : 'text-slate-900'}`}>
            {t('muelles.slots.titulo', { almacen })}
          </h2>
          <p className={`text-[11px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
            {t('muelles.slots.ayuda')}
          </p>
        </header>

        <div className="p-4 space-y-3">
          <div className="flex gap-2">
            <input
              className={campo}
              value={nuevo}
              onChange={(e) => setNuevo(e.target.value)}
              placeholder={t('muelles.slots.nombreNuevo')}
              onKeyDown={(e) => { if (e.key === 'Enter' && nuevo.trim()) crear.mutate() }}
            />
            <button
              type="button"
              disabled={!nuevo.trim() || ocupado}
              onClick={() => crear.mutate()}
              className="px-3 py-1.5 text-[12px] font-semibold rounded bg-sky-500 text-white disabled:opacity-50"
            >
              {t('muelles.slots.anadir')}
            </button>
          </div>

          {aviso && (
            <p className={`px-3 py-2 rounded text-[11px] ${dark ? 'bg-sky-950/50 text-sky-300' : 'bg-sky-50 text-sky-700'}`}>
              {aviso}
            </p>
          )}
          {error && (
            <div className={`px-3 py-2 rounded text-[11px] ${dark ? 'bg-rose-950/50 text-rose-300' : 'bg-rose-50 text-rose-700'}`}>
              {t(`muelles.errores.${error.codigo}`, {
                defaultValue: t('muelles.errores.error_inesperado'),
                ...(error.valores ?? {}),
              })}
              {confirmarBorrado !== null && (
                <button
                  type="button"
                  onClick={() => {
                    const id = slots.find((s) => s.nombre && s.id && borrar.variables?.id === s.id)?.id
                      ?? borrar.variables?.id
                    if (id) borrar.mutate({ id, forzar: true })
                  }}
                  className="ml-2 font-bold underline"
                >
                  {t('muelles.slots.borrarIgual')}
                </button>
              )}
            </div>
          )}

          <ul className={`rounded-lg border ${bdr} divide-y ${dark ? 'divide-white/10' : 'divide-slate-200'}`}>
            {delCatalogo.length === 0 && (
              <li className={`px-3 py-6 text-center text-[11px] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
                {t('muelles.slots.vacio')}
              </li>
            )}
            {delCatalogo.map((s, i) => (
              <li key={s.id} className="flex items-center gap-2 px-3 py-2">
                {editando === s.id ? (
                  <>
                    <input
                      className={campo}
                      value={nombreEdit}
                      autoFocus
                      onChange={(e) => setNombreEdit(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && nombreEdit.trim()) renombrar.mutate({ id: s.id!, nombre: nombreEdit })
                        if (e.key === 'Escape') setEditando(null)
                      }}
                    />
                    <button
                      type="button"
                      disabled={ocupado || !nombreEdit.trim()}
                      onClick={() => renombrar.mutate({ id: s.id!, nombre: nombreEdit })}
                      className="text-[11px] font-semibold text-sky-500 disabled:opacity-50"
                    >
                      {t('muelles.slots.guardar')}
                    </button>
                  </>
                ) : (
                  <>
                    <span className={`flex-1 text-[12px] font-semibold ${dark ? 'text-slate-100' : 'text-slate-900'}`}>
                      {s.nombre}
                    </span>
                    <div className="flex items-center gap-0.5">
                      <button
                        type="button"
                        disabled={i === 0 || ocupado}
                        title={t('muelles.slots.subir')}
                        onClick={() => mover.mutate({ id: s.id!, orden: (delCatalogo[i - 1].orden ?? i) - 1 })}
                        className={`px-1 text-[12px] disabled:opacity-25 ${dark ? 'text-slate-400' : 'text-slate-500'}`}
                      >↑</button>
                      <button
                        type="button"
                        disabled={i === delCatalogo.length - 1 || ocupado}
                        title={t('muelles.slots.bajar')}
                        onClick={() => mover.mutate({ id: s.id!, orden: (delCatalogo[i + 1].orden ?? i + 2) + 1 })}
                        className={`px-1 text-[12px] disabled:opacity-25 ${dark ? 'text-slate-400' : 'text-slate-500'}`}
                      >↓</button>
                      <button
                        type="button"
                        disabled={ocupado}
                        onClick={() => { setEditando(s.id!); setNombreEdit(s.nombre); setError(null) }}
                        className="px-1.5 text-[11px] text-sky-500"
                      >{t('muelles.slots.renombrar')}</button>
                      <button
                        type="button"
                        disabled={ocupado}
                        onClick={() => { setError(null); setConfirmarBorrado(null); borrar.mutate({ id: s.id!, forzar: false }) }}
                        className="px-1.5 text-[11px] text-rose-500"
                      >{t('muelles.slots.borrar')}</button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>

          {ocupado && <Loader size={9} />}

          <button
            type="button"
            onClick={onCerrar}
            disabled={ocupado}
            className={`text-[12px] ${dark ? 'text-slate-300' : 'text-slate-600'}`}
          >
            {t('muelles.slots.cerrar')}
          </button>
        </div>
      </div>
    </div>
  )
}
